import { useEffect, useRef, useState } from 'react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { createSpeechCapture } from '../lib/speechCapture'
import { racquetPath, racquetResponse } from '../lib/racquetApi'
export default function RacquetVoicePanel({
  clubCode,
  match,
  viewId,
  displayTeams,
  t,
  locale,
  onPreview,
  onClose
}) {
  const { memberPortalApiPost: post, memberPortalApiPostBinary: binary } =
    useMemberPortalApi()
  const [transcript, setTranscript] = useState(''),
    [busy, setBusy] = useState(false),
    [recording, setRecording] = useState(false),
    [error, setError] = useState(''),
    [question, setQuestion] = useState('')
  const request = useRef(null),
    capture = useRef(null),
    generation = useRef(0)
  useEffect(
    () => () => {
      generation.current++
      request.current?.abort()
      capture.current?.cancel()
    },
    []
  )
  useEffect(() => {
    generation.current++
    request.current?.abort()
    capture.current?.cancel()
    setBusy(false)
    setRecording(false)
    setQuestion('')
  }, [match.revision, viewId])
  const run = async (fn) => {
    const gen = ++generation.current
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    const timer = setTimeout(() => controller.abort(), 30000)
    setBusy(true)
    setError('')
    try {
      await fn(controller.signal, () => gen === generation.current)
    } catch {
      if (gen === generation.current) setError('voiceFailure')
    } finally {
      clearTimeout(timer)
      if (gen === generation.current) setBusy(false)
    }
  }
  const record = () =>
    run(async (signal, isCurrent) => {
      const mic = await createSpeechCapture({
        onStopRequested: () => {
          if (isCurrent()) setRecording(false)
        }
      })
      if (!isCurrent()) {
        mic.cancel()
        return
      }
      capture.current = mic
      setRecording(true)
      const blob = await mic.result
      setRecording(false)
      if (!blob || !isCurrent()) return
      const data = await binary(
        racquetPath(clubCode, `/matches/${match.id}/voice-transcription`),
        blob,
        { signal }
      ).then(racquetResponse)
      if (isCurrent()) setTranscript(data.transcript)
    })
  return (
    <section
      className="rp-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('voiceReview')}
    >
      <h2>{t('voiceReview')}</h2>
      <button
        onClick={() => {
          generation.current++
          request.current?.abort()
          capture.current?.cancel()
          onClose()
        }}
      >
        {t('cancel')}
      </button>
      {recording ? (
        <button onClick={() => capture.current?.stop()}>{t('stop')}</button>
      ) : (
        <button disabled={busy} onClick={record}>
          {t('listen')}
        </button>
      )}
      <label>
        {t('transcript')}
        <textarea
          maxLength={4000}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
        />
      </label>
      {error && <p role="alert">{t(error)}</p>}
      {question && (
        <p role="status">
          <strong>{t('clarify')}</strong> {question}
        </p>
      )}
      <button
        disabled={busy || !transcript.trim()}
        onClick={() =>
          run(async (signal, isCurrent) => {
            const p = await post(
              racquetPath(
                clubCode,
                `/matches/${match.id}/voice-interpretation`
              ),
              {
                transcript,
                language: locale,
                revision: match.revision,
                viewId,
                displayTeams
              },
              { signal }
            ).then(racquetResponse)
            if (!isCurrent()) return
            if (p.question) setQuestion(p.question)
            else onPreview(p)
          })
        }
      >
        {t(busy ? 'interpreting' : 'interpret')}
      </button>
    </section>
  )
}
